import { useMemo } from 'react';
import {
  evaluateColorRule,
  parseColorRule,
  type AnalysisColorRule,
  type ColorRule,
  type EvaluatedColor,
} from '@citizen-walk/shared-types';
import { useDistrict } from '../district';
import type { PostMinimal } from './usePosts';

/**
 * docs/105 残課題「colorRule バッジ」— 投稿一覧 (ListPanel) / 投稿詳細 (PostDetailModal) に
 * 地区の色ルール (`District.mapDisplay.colorRule`) 由来の「種別バッジ (色 + ラベル + (あれば) アイコン)」
 * を表示するための共通 helper / hook / 表示 component。
 *
 * 旧 v1 では `useColorRule(district, post)` (`old2/.../hooks/useColorRule.ts`) が
 * 地図ピン・投稿リスト・投稿詳細を統一駆動していた。v2 では地図 marker 色は
 * `features/map/markerStyle.ts` (`getMarkerStyle` → `evaluateColorRule`) で対応済だが、
 * 一覧 / 詳細はバッジ未表示だった (ListTab L344-385 / PostDetailModal L159-166 の踏襲積み残し)。
 *
 * 設計方針:
 *   - **評価ロジックは新規に書かない**。`evaluateColorRule` (shared-types、`markerStyle.ts` が
 *     marker 色決定で使うのと同一関数) を素直に再利用する。value 形式 (buckets) /
 *     quadrant 形式 (cells) の両対応は `evaluateColorRule` 側に閉じている。
 *   - colorRule の取得は `features/map/useColorRule.ts` と同 logic を posts feature 内に置く
 *     (`useDistrict` + `parseColorRule`)。map feature の `useColorRule` を import すると
 *     map → posts (markerStyle) → … の循環依存になるため、district feature のみ依存して再構成。
 *   - colorRule 未設定 (= `useDistrictColorRule()` が null) の地区ではバッジを描画しない
 *     (旧 v1 では mode-default の 1 色 + 空ラベルになり得たが、一覧 / 詳細では「色だけのドット +
 *     空ラベル」はノイズなので非表示が望ましい)。
 */

/**
 * 地区の有効な ColorRule (`kind: 'value'|'quadrant'` 形式) を解決する hook。
 *
 * `features/map/useColorRule.ts` と同じ priority:
 *   1. `District.mapDisplay.colorRule` が `kind` を持つ → `parseColorRule` で安全変換して採用
 *   2. それ以外 (未設定 / `AnalysisColorRule` の colorAxis 形式) → null
 *
 * null の場合、呼び出し側 (ListPanel / PostDetailModal) はバッジを描画しない。
 */
export function useDistrictColorRule(): ColorRule | null {
  const district = useDistrict();
  return useMemo(() => {
    const raw = district?.mapDisplay?.colorRule;
    if (!raw) return null;
    // `kind` を持つ新 ColorRule のみ採用 (旧 AnalysisColorRule の colorAxis 形式は対象外)。
    if ('kind' in (raw as object)) {
      return parseColorRule(raw as unknown) ?? null;
    }
    void (raw as AnalysisColorRule);
    return null;
  }, [district?.mapDisplay?.colorRule]);
}

/**
 * Post を ColorRule で評価して色・ラベル・(あれば) アイコンを返す純関数。
 * `markerStyle.ts#getMarkerStyle` と同じく `evaluateColorRule` を素直に呼ぶだけ。
 *
 * - value 形式: `fieldKey` (extraFields.<key> 等) を単一フィールド評価
 * - quadrant 形式: `xKey` / `yKey` を 2 フィールド評価 (ECW の Happy×Health 等)
 *
 * rule が null/undefined のときは null を返す (呼び出し側でバッジ非表示)。
 */
export function evaluatePostColor(
  post: PostMinimal,
  rule: ColorRule | null | undefined,
): EvaluatedColor | null {
  if (!rule) return null;
  return evaluateColorRule(rule, post as unknown as Record<string, unknown>);
}

export interface ColorBadgeProps {
  /** 評価済の色・ラベル・アイコン (`evaluatePostColor` の戻り値)。null なら何も描画しない。 */
  evaluated: EvaluatedColor | null;
  /**
   * 表示形態:
   *   - `'pill'` (既定): 色付きの丸薬型 chip + ラベル (投稿詳細ヘッダ向け)
   *   - `'dot'`: 色ドット + 横にラベル (一覧の各行 / カード向け、旧 ListItem 踏襲)
   */
  variant?: 'pill' | 'dot';
  /** 追加の className (配置調整用)。 */
  className?: string;
}

/**
 * colorRule 評価結果を「色 + ラベル + (あれば) アイコン」のバッジで描画する presentational component。
 *
 * 動的色 (admin が任意に設定する #RRGGBB) は Tailwind の semantic class では表現できないため、
 * 背景 / 文字色のみ inline style で渡す (既存 `PostDetailModal#Chip` / `MapLegend` の色ドットと同方針、
 * CLAUDE.md「inline style は禁止 (既存 features 除く)」の例外: 動的色は値が実行時にしか決まらない)。
 */
export function ColorBadge({ evaluated, variant = 'pill', className = '' }: ColorBadgeProps) {
  if (!evaluated) return null;
  const { color, label, icon } = evaluated;
  // ラベルが空 (fallback ラベル未設定等) で icon も無いなら描画する意味が無い (色だけのノイズ回避)。
  if (!label && !icon) return null;

  if (variant === 'dot') {
    return (
      <span
        className={`inline-flex items-center gap-1 min-w-0 ${className}`}
        data-testid="color-badge"
      >
        <span
          className="inline-block w-2.5 h-2.5 rounded-full shrink-0 border border-slate-200"
          style={{ backgroundColor: color }}
          aria-hidden="true"
          data-testid="color-badge-dot"
        />
        {(icon || label) && (
          <span className="text-xs text-slate-500 truncate">
            {icon ? `${icon} ` : ''}
            {label}
          </span>
        )}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium text-white ${className}`}
      style={{ backgroundColor: color }}
      data-testid="color-badge"
    >
      {icon ? `${icon} ` : ''}
      {label}
    </span>
  );
}
